import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  AuthErrorCodes,
  signOut,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

import useStyles from "../../styles/pages/LoginPage";
import { auth, db } from "../../stores/FirebaseStore";

/**
 * Login de anunciante (padrão) ou comprador/revenda (`isBuyer`).
 * Uso: `<LoginPage />` ou `<LoginPage isBuyer />`.
 */
const LoginPage = ({ isBuyer = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        setGoogleLoading(true);
        const result = await getRedirectResult(auth);
        if (!result) {
          return;
        }

        const fbUser = result.user;
        const userRef = doc(db, "users", fbUser.uid);
        const userSnap = await getDoc(userRef);

        if (isBuyer) {
          if (userSnap.exists()) {
            const data = userSnap.data();
            if (data.isBuyer !== true) {
              await signOut(auth);
              setError(
                "Esta conta não está cadastrada como comprador/revenda. Use o login de anunciante.",
              );
              return;
            }
            await setDoc(
              userRef,
              { lastLogin: serverTimestamp() },
              { merge: true },
            );
          } else {
            await setDoc(userRef, {
              name: fbUser.displayName,
              email: fbUser.email,
              phone: fbUser.phoneNumber || "",
              createdAt: serverTimestamp(),
              isGoogleAccount: true,
              isBuyer: true,
              lastLogin: serverTimestamp(),
            });
          }
          navigate("/comprador/vitrine", { replace: true });
          return;
        }

        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data.isBuyer === true) {
            await setDoc(
              userRef,
              { lastLogin: serverTimestamp() },
              { merge: true },
            );
            navigate("/comprador/vitrine", { replace: true });
            return;
          }
          await setDoc(
            userRef,
            { lastLogin: serverTimestamp() },
            { merge: true },
          );
        } else {
          await setDoc(userRef, {
            name: fbUser.displayName,
            email: fbUser.email,
            phone: fbUser.phoneNumber || "",
            createdAt: serverTimestamp(),
            isGoogleAccount: true,
            isBuyer: false,
            lastLogin: serverTimestamp(),
          });
        }
        navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error("Erro no login com Google:", err);
        setError("Erro ao fazer login com Google. Tente novamente.");
      } finally {
        setGoogleLoading(false);
      }
    };

    checkRedirectResult();
  }, [navigate, isBuyer]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value);
  };

  const handleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const userRef = doc(db, "users", cred.user.uid);
      const userSnap = await getDoc(userRef);

      if (isBuyer) {
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            name: cred.user.displayName || "",
            email: cred.user.email,
            phone: cred.user.phoneNumber || "",
            createdAt: serverTimestamp(),
            isGoogleAccount: false,
            isBuyer: true,
            lastLogin: serverTimestamp(),
          });
          navigate("/comprador/vitrine", { replace: true });
          return;
        }
        const buyerData = userSnap.data();
        if (buyerData.isBuyer !== true) {
          await signOut(auth);
          setError("Esta conta não está cadastrada como comprador/revenda.");
          return;
        }
        await setDoc(
          userRef,
          { lastLogin: serverTimestamp() },
          { merge: true },
        );
        navigate("/comprador/vitrine", { replace: true });
        return;
      }

      if (userSnap.exists() && userSnap.data().isBuyer === true) {
        await setDoc(
          userRef,
          { lastLogin: serverTimestamp() },
          { merge: true },
        );
        navigate("/comprador/vitrine", { replace: true });
        return;
      }

      if (userSnap.exists()) {
        await setDoc(
          userRef,
          { lastLogin: serverTimestamp() },
          { merge: true },
        );
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Erro ao fazer login:", err.code, err.message);

      switch (err.code) {
        case AuthErrorCodes.INVALID_EMAIL:
          setEmailError("Email inválido");
          break;
        case AuthErrorCodes.USER_DELETED:
          setEmailError("Usuário não encontrado");
          break;
        case AuthErrorCodes.INVALID_PASSWORD:
          setPasswordError("Senha incorreta");
          break;
        case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
          setError("Muitas tentativas. Tente novamente mais tarde.");
          break;
        default:
          setError("Erro ao fazer login. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setEmailError("");
    setPasswordError("");
    setError(null);

    let isValid = true;

    if (!email) {
      setEmailError("Email é obrigatório");
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Email inválido");
      isValid = false;
    }

    if (!password) {
      setPasswordError("Senha é obrigatória");
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError("Senha deve ter pelo menos 6 caracteres");
      isValid = false;
    }

    if (isValid) {
      handleLogin();
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    const provider = new GoogleAuthProvider();

    try {
      await signInWithRedirect(auth, provider);
    } catch (err) {
      console.error("Erro ao iniciar login com Google:", err);
      setError("Erro ao iniciar login com Google. Tente novamente.");
    }
  };

  return (
    <div>
      <Container component="main" maxWidth="xs">
        <Box className={classes.container}>
          <Paper elevation={3} className={classes.paper}>
            <Box className={classes.titleContainer}>
              <Typography component="h1" variant="h5" className={classes.title}>
                {isBuyer ? "Acesso comprador / revenda" : "Bem-vindo de volta"}
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                {isBuyer
                  ? "Entre para visualizar a vitrine de veículos"
                  : "Entre com suas credenciais para acessar sua conta"}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              className={classes.form}
              aria-label="Formulário de login"
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
                className={classes.textField}
                aria-describedby="email-error"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Senha"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!passwordError}
                helperText={passwordError}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                className={classes.textField}
                aria-describedby="password-error"
              />

              <Box sx={{ textAlign: "right", mt: 1 }}>
                <Link
                  component={RouterLink}
                  to="/recuperar-senha"
                  variant="body2"
                  className={classes.forgotPassword}
                >
                  Esqueceu sua senha?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                className={classes.button}
                disabled={loading || googleLoading}
                aria-busy={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Entrar"
                )}
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" className={classes.dividerText}>
                  ou
                </Typography>
              </Divider>

              <Button
                onClick={handleGoogleLogin}
                fullWidth
                variant="outlined"
                className={classes.googleButton}
                disabled={googleLoading}
                startIcon={
                  <img
                    src="/google-icon.svg"
                    alt="Google"
                    width={20}
                    height={20}
                    aria-hidden="true"
                  />
                }
              >
                {googleLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Entrar com Google"
                )}
              </Button>

              <Box sx={{ textAlign: "center", mt: 1 }}>
                <Typography variant="body2">
                  Não tem uma conta?{" "}
                  <Link
                    component={RouterLink}
                    to={isBuyer ? "/comprador/registro" : "/registro"}
                    className={classes.registerLink}
                  >
                    {isBuyer
                      ? "Registre-se como comprador/revenda"
                      : "Registre-se"}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </div>
  );
};

export default LoginPage;
